import React, { useState, useEffect } from 'react';
import { authService } from '../../services/api';
import axios from 'axios';
import AuthCard from './AuthCard';
import { Link, useParams, useLocation } from 'react-router-dom';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { CircularProgress } from '@mui/material';
import { homeAfterSignIn } from '../../utils/homeAfterSignIn';

// S-4 pt 5.2: the only address sign-in returns to is a file address on this site (/uploads/...), checked as the browser
// will read it: an encoded '..' (%2e%2e) can't lead out of /uploads/. Removed 23 Oct 2026 (S-4 pt 5.2, PR #8 review):
// checking the text as typed, so /uploads/%2e%2e/dashboard passed.
const isFileReturnAddress = (address) => {
  if (typeof address !== 'string' || !address.startsWith('/uploads/')) return false;
  try {
    const url = new URL(address, window.location.origin);
    return url.origin === window.location.origin && url.pathname.startsWith('/uploads/')
      && !decodeURIComponent(url.pathname).includes('..');
  } catch (e) {
    return false;
  }
};
// The file address is kept for 15 minutes in this tab, so a sign-in much later in the same tab doesn't jump to it.
const RETURN_TO_KEY = 'beetsolReturnTo';
const RETURN_TO_MS = 15 * 60 * 1000;

const LearnerLoginCard = () => {
  // Workspace slug from the URL (/learn/:slug). Drives which workspace the
  // learner is signing into; used to resolve branding + scope the auth call.
  const { slug: urlSlug } = useParams();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  // A session ended because the account was deactivated opens on the inactive card (S-6.1 pt 5.2): the server leaves a
  // short-lived 'beetsol_signin_notice=inactive' cookie, read here and cleared below.
  const [status, setStatus] = useState(() => (
    /(?:^|;\s*)beetsol_signin_notice=inactive(?:;|$)/.test(document.cookie) ? 'internal-inactive-error' : 'email-input'
  )); 
  // Possible statuses: 'email-input', 'password-input', 'loading', 'locked', 
  // 'email-not-found', 'magic-link-sent', 'invitation-pending','internal-inactive-error','external-inactive-error', 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [lockoutMinutes, setLockoutMinutes] = useState(0);
  const [lockoutTimer, setLockoutTimer] = useState(null);
  const [clientSlug, setClientSlug] = useState(urlSlug || null);
  const [firstName, setFirstName] = useState('');
  const [correctDomain, setCorrectDomain] = useState(null);
  const location = useLocation();
  // S-4 pt 5.2: a file address opened signed out comes here as ?next=/uploads/...; it is kept for this tab (the email step
  // may move to the workspace's own page) and used after sign-in. Only a file address on this site is kept.
  useEffect(() => {
    const next = new URLSearchParams(location.search).get('next');
    if (isFileReturnAddress(next)) {
      try { sessionStorage.setItem(RETURN_TO_KEY, JSON.stringify({ to: next, at: Date.now() })); } catch (e) { /* storage unavailable: home page instead */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The sign-in page these cards go back to: the page itself, by its own address: a workspace's page (/learn/acme), or
  // the plain page (/login or /), which names none. Going back clears the card's message, so the email step starts clean.
  // Removed 23 Oct 2026 (S-6.1 pt 5.2): /learn/null on a page that names no workspace, and the error card's link to
  // /learner-login, a page that doesn't exist. Removed 23 Oct 2026 (S-6.1 pts 2.1.a, 5.2, PR #8 review): the workspace
  // the logo came from, the session's last one, so on the plain page the links led to that workspace's page (an ended
  // trial's, whose email step refuses every email).
  const signInHome = urlSlug ? `/learn/${urlSlug}` : location.pathname;
  const backToEmailStep = () => {
    setErrorMsg('');
    setStatus('email-input');
  };

  useEffect(() => {
    if (/(?:^|;\s*)beetsol_signin_notice=/.test(document.cookie)) {
      document.cookie = 'beetsol_signin_notice=; Max-Age=0; path=/';
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prefilledEmail = params.get('email');

    if (prefilledEmail) {
      // Clean URL without reloading
      window.history.replaceState({}, '', `/learn/${urlSlug}`);
      // Pre-fill and auto-submit email
      setFormData(prev => ({ ...prev, email: prefilledEmail }));
      autoSubmitEmail(prefilledEmail);
    }
  }, []);

  const autoSubmitEmail = async (emailValue) => {
    setStatus('loading');
    setErrorMsg('');

    try {
      // Pass urlSlug so backend resolves the right client immediately
      // This prevents the redirect loop on /learn/:slug?email=... landing
      const response = await authService.learnerLoginEmail(emailValue, urlSlug);

      if (response.data.success) {
        setFirstName(`, ${response.data.userFirstName}` || '');
        setSubmittedEmail(emailValue);

        if (response.data.userType === 'internal' && response.data.active === true) {
          setFormData({ email: emailValue, password: '' });
          setAttemptsLeft(3);
          setStatus('password-input');                // ← lands here directly
        } else if (response.data.userType === 'external' && response.data.active === true) {
          setStatus('magic-link-sent');               // ← or here for external
        }
      } else {
        // Handle all your existing error statuses normally
        handleEmailResponse(response.data, emailValue);
      }
    } catch (error) {
      setStatus('error');
      setErrorMsg(error.response?.data?.message || 'Failed to check email. Please try again.');
    }
  };

  /* ── Email submit ── */
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email.trim()) {
      setErrorMsg('Please enter a valid email address');
      return;
    }
    await autoSubmitEmail(formData.email);
  };

  // Shared response handler to avoid duplication
  const handleEmailResponse = (data, emailValue) => {
    setSubmittedEmail(emailValue);
    if (data.userType === 'not-found') {
      setStatus('email-not-found');
    } else if (data.userType === 'wrong-client') {
      localStorage.setItem('clientSlug', data.correctSlug);
      console.log('Redirecting to correct client portal:', data);
      window.location.href = `/learn/${data.correctSlug}?email=${encodeURIComponent(emailValue)}`;
      //setStatus('error');
    } else if (data.userType === 'internal' && data.active == 'invited') {
      setStatus('internal-activation-pending');
    } else if (data.userType === 'external' && data.active == 'invited') {
      setStatus('external-activation-pending');
    } else if (data.userType === 'internal' && !data.active) {
      setErrorMsg(data.message || 'Your account is inactive');
      setStatus('internal-inactive-error');
    } else if (data.userType === 'external' && !data.active) {
      setErrorMsg(data.message || 'Your account is inactive');
      setStatus('external-inactive-error');
    } else {
      setErrorMsg(data.message || 'Failed to check email');
      setStatus('error');
    }
  };

  /* ── Password submit ── */
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!formData.password.trim()) {
      setAttemptsLeft(0);
      setErrorMsg('Please enter your password');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const response = await authService.learnerLoginPassword(formData.email, formData.password);

      if (response.data.success) {
        localStorage.setItem('isAuthenticated', 'true');
        if (response.data.user) {
        localStorage.setItem('userData', JSON.stringify(response.data.user));
        }
        // Password correct: the admin site (admin.beetsol.com) opens on Workspaces for a super admin, every other
        // sign-in on the dashboard (K11 pt 5.1, utils/homeAfterSignIn.js, which the activation page uses too). Removed
        // 23 Oct 2026 (K11 pt 5.1): the admin site opening on /dashboard; (PR #8 review) the rule written out here only.
        // S-4 pt 5.2: back to the file address that brought them to sign in, if any.
        let returnTo = null;
        try {
          const kept = JSON.parse(sessionStorage.getItem(RETURN_TO_KEY) || 'null');
          sessionStorage.removeItem(RETURN_TO_KEY);
          if (kept && Date.now() - Number(kept.at) < RETURN_TO_MS) returnTo = kept.to;
        } catch (e) { /* storage unavailable or unreadable: home page instead */ }
        if (isFileReturnAddress(returnTo)) {
          window.location.href = returnTo;
          return;
        }
        window.location.href = homeAfterSignIn(response.data.user);
      } else {
        // Password incorrect or account locked
        if (response.data.userType === 'locked') {
          setLockoutMinutes(response.data.remainingMinutes);
          setStatus('locked');
          // Start countdown timer
          startLockoutTimer(response.data.remainingMinutes);
        } else if (response.data.userType === 'inactive') {
          // A Deactivated account sees the agreed inactive card, as at the email step (S-6.1 pt 5, agreed 17 Oct).
          // Removed 18 Oct 2026 (S-6.1 pt 5): an empty red line under the password.
          setErrorMsg(response.data.message || 'Your account is inactive');
          setStatus('internal-inactive-error');
        } else {
          // Wrong password but not locked
          setAttemptsLeft(response.data.attemptsLeft || 0);
          setErrorMsg(response.data.message || 'Incorrect password');
          setStatus('password-input');
        }
      }
    } catch (error) {
      // A refusal (a switched-off workspace or an ended free trial: 403) or a failure shows its own message, not the
      // attempts line (S-14, K11 pt 2). Removed 18 Oct 2026 (S-14): "Incorrect password. 3 attempts remaining." for it.
      setAttemptsLeft(0);
      setStatus('password-input');
      setErrorMsg(error.response?.data?.message || 'Login failed. Please try again.');
    }
  };

  /* ── Resend magic link ── */
  const handleResendMagicLink = async () => {
    setErrorMsg('');
    setStatus('loading');

    try {
      // With the page's workspace, as the first send (S-6.1 pt 5).
      const response = await authService.learnerLoginEmail(submittedEmail, urlSlug);
      if (response.data.success) {
        setStatus('magic-link-sent');
        setErrorMsg('A new magic link has been sent. Check your inbox.');
      } else {
        // Any other answer (the account deactivated since the first send, say) shows what the first send would show now:
        // the agreed inactive card, or the page's card for that answer (S-6.1 pt 5). Removed 18 Oct 2026 (S-6.1 pt 5):
        // staying on "Check your email", which shows no message, so it still said a link had been sent.
        handleEmailResponse(response.data, submittedEmail);
      }
    } catch (error) {
      // A refusal (a switched-off workspace or an ended free trial: 403) or a failure shows its message, as the first
      // send does (S-14, S-19 (f)). Removed 18 Oct 2026 (S-14): staying on "Check your email", as above.
      setStatus('error');
      setErrorMsg(error.response?.data?.message || 'Failed to resend magic link');
    }
  };

  /* ── Resend invitation ── */
  const handleResendInvitation = async () => {
    setErrorMsg('');
    setStatus('loading');

    try {
      const response = await authService.learnerLoginEmail(submittedEmail);
      if (response.data.success) {
        setStatus('external-activation-pending');
        setErrorMsg('Invitation has been sent. Check your inbox.');
      } else {
        setStatus('external-activation-pending');
        setErrorMsg(response.data.message || 'Failed to resend invitation');
      }
    } catch (error) {
      setStatus('external-activation-pending');
      setErrorMsg(error.response?.data?.message || 'Failed to resend invitation');
    }
  };

   /* ── Resend invitation ── */
  const handleResendInternalInvitation = async () => {
    setErrorMsg('');
    setStatus('loading');

    try {
      const response = await authService.learnerLoginEmail(submittedEmail);
      if (response.data.success) {
        setStatus('internal-activation-pending');
        setErrorMsg('Invitation has been sent. Check your inbox.');
      } else {
        setStatus('internal-activation-pending');
        setErrorMsg(response.data.message || 'Failed to resend invitation');
      }
    } catch (error) {
      setStatus('internal-activation-pending');
      setErrorMsg(error.response?.data?.message || 'Failed to resend invitation');
    }
  };

  /* ── Start lockout countdown timer ── */
  const startLockoutTimer = (minutes) => {
    if (lockoutTimer) clearInterval(lockoutTimer);

    let remainingTime = minutes * 60; // Convert to seconds

    const timer = setInterval(() => {
      remainingTime--;
      setLockoutMinutes(Math.ceil(remainingTime / 60));

      if (remainingTime <= 0) {
        clearInterval(timer);
        // Reset to email input after lockout expires
        setStatus('email-input');
        setFormData({ email: '', password: '' });
        setErrorMsg('Your account has been unlocked. Please try again.');
      }
    }, 1000);

    setLockoutTimer(timer);
  };

  /* ── Clean up timer on unmount ── */
  useEffect(() => {
    return () => {
      if (lockoutTimer) clearInterval(lockoutTimer);
    };
  }, [lockoutTimer]);

  /* ── Logo fetch ── */
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        // Pass the URL slug so the backend resolves the right workspace branding
        // (when landing on /learn/:slug directly). Backend should accept ?slug=.
        const res = await axios.get('/api/settings/logo', urlSlug ? { params: { slug: urlSlug } } : undefined);
        if (res.data.success && res.data.logoUrl) {
          setLogoUrl(res.data.logoUrl);
        } else {
          setLogoUrl('/default-logo.png');
        }
        // Prefer the slug from the URL; fall back to whatever the API returns.
        setClientSlug(urlSlug || res.data.slug || null);
      } catch (err) {
        console.error('Failed to fetch logo', err);
      }      
    };

    fetchLogo();
  }, []);

  /* ────────────────────────────── Loading ────────────────────────────── */
  if (status === 'loading') {
    return (
      <AuthCard logoUrl={logoUrl}>
        <div className="ac-body" style={{ textAlign: 'center', padding: '40px 0' }}>
          <CircularProgress />
          <div className="ac-title" style={{ marginTop: '20px' }}>
            {status === 'loading' && 'Processing...'}
          </div>
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }

  /* ────────────────────────────── Email Input ────────────────────────────── */
  if (status === 'email-input') {
    return (
      <AuthCard logoUrl={logoUrl}>
        <div className="ac-body">
          <div className="ac-top">
            <div className="ac-title">Sign in</div>
            <div className="ac-sub">Enter your email to access your learning account.</div>
          </div>
          <form onSubmit={handleEmailSubmit}>
            <div className="ac-field">
              <div className="ac-field-label">Email</div>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="ac-field-input"
                placeholder="e.g. john@company.com"
                required
              />
              {errorMsg && <div className="ac-field-err">{errorMsg}</div>}
            </div>            
            <button type="submit" className="ac-btn-primary">
              Continue
            </button>
          </form>
          <div className="ac-divider">
            <div className="ac-divider-line" />
            <div className="ac-divider-text">or</div>
            <div className="ac-divider-line" />
          </div>
            <div className="ac-redirect">
                Don't have an account? <Link to={`/signup/${clientSlug}`} className="ac-link">Sign up</Link>           
            </div>          
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }

  /* ────────────────────────────── Password Input ────────────────────────────── */
  if (status === 'password-input') {
    return (
      <div>
        <AuthCard logoUrl={logoUrl} tagline="Internal account">
          <div className="ac-body">
            <div className="ac-top">
              <div className="ac-title">Welcome back{firstName}</div>
              <div className="ac-sub">Enter your password to sign in.</div>
            </div>
            <form onSubmit={handlePasswordSubmit}>
              <div className="ac-field">
                <div className="ac-field-label">Email</div>
                <input
                  type="email"
                  value={submittedEmail}
                  className="ac-field-input"
                  readOnly
                  required
                />
              </div>
              <div className="ac-field">
                <div className="ac-field-label">Password</div>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={`ac-field-input ${errorMsg ? 'error'  : ''}`}
                  placeholder="Enter your password"
                  required
                />
                {errorMsg && (
                  <div className="ac-field-err" style={{ color: 'var(--td)' }}>
                      {/* The attempts line answers a wrong password; any other answer shows its own message (S-6.1 pt 5,
                          S-14). Removed 18 Oct 2026 (S-6.1 pt 5): an empty line for an answer with no attempts left. */}
                      {attemptsLeft > 0 ? ` Incorrect password. ${attemptsLeft} attempt${attemptsLeft !== 1 ? 's' : ''} remaining.` : errorMsg}
                  </div>
                  )}
              </div>
              <div style={{ textAlign:'right',marginBottom:'20px',fontSize:'14px' }}>
                <Link to="/forgot" className="ac-link">Forgot password?</Link>
              </div>
              
              <button type="submit" className="ac-btn-primary">
                Sign In
              </button>
            </form>          
            <div className="ac-field-link">
              <Link to={signInHome} onClick={backToEmailStep} className="ac-link">
                ← Use a different email
              </Link>
            </div>
          </div>
          <div className="ac-footer" />
          <div className="ac-field-link">
            By signing in, you acknowledge and agree to Beetsol’s<br/>
            <Link to={`https://beetsol.com/terms`} target="_blank" className="ac-link">Terms of Use</Link> and <Link to={`https://beetsol.com/privacy`} target="_blank" className="ac-link">Privacy Policy</Link>
          </div>
        </AuthCard>
        
         

      </div>
    );
  }

  /* ────────────────────────────── Account Locked ────────────────────────────── */
  if (status === 'locked') {
    return (
      <AuthCard logoUrl={logoUrl}>
        <div className="ac-top">
          <div className="ac-icon ac-icon-error">
            <span style={{ fontSize: '24px' }}>
              <CancelOutlinedIcon />
            </span>
          </div>
          <div className="ac-title">Account temporarily locked</div>
          <div className="ac-sub">Too many failed sign-in attempts. Your account has been locked for security.</div>
        </div>
        <div className="ac-body">
          <div className="ac-box ac-box-error">
            <div className="ac-box-title">Try again in {lockoutMinutes} minute{lockoutMinutes !== 1 ? 's' : ''}</div>
            <div className="ac-box-sub">
              Or reset your password to sign in immediately.
            </div>
          </div>
          <button type="submit" onClick={() => { window.location.href = '/forgot'; }} className="ac-btn-primary">
              Reset password
          </button>
          <div className="ac-field-link">
            <Link to={signInHome} onClick={backToEmailStep} className="ac-link">
              ← Use a different email
            </Link>
          </div>
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }

  /* ────────────────────────────── Email Not Found ────────────────────────────── */
  if (status === 'email-not-found') {
    return (
      <AuthCard logoUrl={logoUrl}>
        <div className="ac-top">
          <div className="ac-icon ac-icon-error">
            <span style={{ fontSize: '24px' }}>
              <WarningAmberOutlinedIcon />
            </span>
          </div>
          <div className="ac-title">No account found</div>
          <div className="ac-sub">We couldn’t find an account for<br/> <strong>{submittedEmail}</strong></div>
        </div>
        <div className="ac-body">
          <div className="ac-box ac-box-info">
            <div className="ac-box-sub">If you’re a customer, <Link to={`/signup/${clientSlug}`} className="ac-link">sign up here</Link>.<br/>If you're an employee, contact your administrator for an invitation.</div>
            

          </div>
          <div className="ac-field-link">
            <Link to={signInHome} onClick={backToEmailStep} className="ac-link">
              Try a different email
            </Link>
          </div>
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }

  /* ────────────────────────────── Magic Link Sent ────────────────────────────── */
  if (status === 'magic-link-sent') {
    return (
      <AuthCard logoUrl={logoUrl}>
        <div className="ac-top">
          <div className="ac-icon ac-icon-success">
            <span style={{ fontSize: '24px' }}>
              <EmailOutlinedIcon />
            </span>
          </div>
          <div className="ac-title">Check your email</div>
          <div className="ac-sub">We sent a magic link to <strong>{submittedEmail}</strong>. Click it to sign in — it expires in 15 minutes.</div>
        </div>
        <div className="ac-body">
          <div className="ac-field-link">
            Didn’t receive it? <Link className="ac-link" onClick={handleResendMagicLink}>Resend</Link>
          </div>
          <div className="ac-field-link">
            <Link to={signInHome} onClick={backToEmailStep} className="ac-link">
            ← Use a different email
            </Link>
          </div> 
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }
  /* ────────────────────────────── External Invitation Pending ────────────────────────────── */
  if (status === 'external-activation-pending') {
    return (
        <AuthCard logoUrl={logoUrl}>
            <div className="ac-top">
                <div className="ac-icon ac-icon-warn">
                    <span style={{ fontSize: '24px' }}>
                    <WarningAmberOutlinedIcon />
                    </span>
                </div>
                <div className="ac-title">Invitation pending</div>
                <div className="ac-sub">Your account was created but hasn’t been activated yet. We’ve resent your activation link.</div>
                </div>
                <div className="ac-body">
                <div className="ac-box ac-box-warn">
                    <div className="ac-box-title">Activation link resent</div>
                    <div className="ac-box-sub">Check your email for a new magic link. It expires in 72 hours.</div>
                </div>
                <div className="ac-field-link">
                    <Link to={signInHome} onClick={backToEmailStep} className="ac-link">
                    ← Use a different email
                    </Link>
                </div>                
            </div>
            <div className="ac-footer" />
        </AuthCard>
    );
  }
  /* ────────────────────────────── Invitation Pending ────────────────────────────── */
  if (status === 'internal-activation-pending') {
    return (
      <AuthCard logoUrl={logoUrl}>
        <div className="ac-top">
          <div className="ac-icon ac-icon-warn">
            <span style={{ fontSize: '24px' }}>
              <WarningAmberOutlinedIcon />
            </span>
          </div>
          <div className="ac-title">Activation required</div>
          <div className="ac-sub">Your account is pending activation</div>
        </div>
        <div className="ac-body">
          <div className="ac-box ac-box-warn">
            <div className="ac-box-title">Invitation pending</div>
            <div className="ac-box-sub">We've sent an invitation to <strong>{submittedEmail}</strong>. Click the link in your email to activate your account and set a password.</div>
          </div>
          {errorMsg && <div className="ac-field-err" style={{ color: 'var(--t2)' }}>{errorMsg}</div>}
          <div className="ac-field-link">
            Didn't receive it? <Link className="ac-link" onClick={handleResendInvitation}>Resend invitation</Link>
          </div>
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }
  /* ────────────────────────────── Internal Inactive Error ────────────────────────────── */
  if (status === 'internal-inactive-error') {
    return (
      <AuthCard logoUrl={logoUrl}>
        <div className="ac-top">
          <div className="ac-icon ac-icon-error">
            <span style={{ fontSize: '24px' }}>
              <CancelOutlinedIcon />
            </span>
          </div>
          {/* A Deactivated account sees the agreed words only, as at every other step (S-6.1 pt 5, agreed 17 Oct).
              Removed 17 Oct 2026 (S-6.1 pt 5): this page's own wording, "Account inactive. Your account has been
              deactivated by your administrator.", and its "Access restricted" box. */}
          <div className="ac-title">Your account is inactive.</div>
          <div className="ac-sub">Please contact your administrator.</div>
        </div>
        <div className="ac-body">
          <div className="ac-field-link">
            <Link to={signInHome} onClick={backToEmailStep} className="ac-link">
              ← Use a different email
            </Link>
          </div>
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }
  
  /* ────────────────────────────── External Inactive Error ────────────────────────────── */
  if (status === 'external-inactive-error') {
    return (
      <AuthCard logoUrl={logoUrl}>
        <div className="ac-top">
          <div className="ac-icon ac-icon-error">
            <span style={{ fontSize: '24px' }}>
              <CancelOutlinedIcon />
            </span>
          </div>
          {/* The agreed words only (S-6.1 pt 5, agreed 17 Oct). Removed 17 Oct 2026 (S-6.1 pt 5): "Account inactive.
              Your account has been deactivated. Contact your administrator for access.", and its "Access restricted"
              box. */}
          <div className="ac-title">Your account is inactive.</div>
          <div className="ac-sub">Please contact your administrator.</div>
        </div>
        <div className="ac-body">
          <div className="ac-field-link">
            <Link to={signInHome} onClick={backToEmailStep} className="ac-link">
              ← Use a different email
            </Link>
          </div>
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }

  /* ────────────────────────────── Error ────────────────────────────── */
  if (status === 'error') {
    return (
      <AuthCard logoUrl={logoUrl}>
        <div className="ac-top">
          <div className="ac-icon ac-icon-error">
            <span style={{ fontSize: '24px' }}>
              <WarningAmberOutlinedIcon />
            </span>
          </div>
          <div className="ac-title">Unable to sign in</div>
        </div>
        <div className="ac-body">
          <div className="ac-box ac-box-error">
            <div className="ac-box-title">Error</div>
            <div className="ac-box-sub">{errorMsg}</div>
          </div>
          <div className="ac-field-link">
            <Link to={signInHome} onClick={backToEmailStep} className="ac-link">
              Try again
            </Link>
          </div>
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }
};

export default LearnerLoginCard;